import { Fragment, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  CircularProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Collapse,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PaidIcon from "@mui/icons-material/Paid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import * as XLSX from "xlsx"; // Needed here for the file upload handler
import TutorAccordion from "../components/TutorAccordion";
import { processAndDownloadPayroll } from "../services/payrollService";
import { downloadTutorPayrollBreakdown } from "../services/payrollBreakdownService";
import { hourlyRateRule, isValidHourlyRate, maxRateFor } from "../utils/rates";

export default function TutorsPage() {
  const { profile } = useAuth();
  // The viewer role is read-only: no payroll (it marks sessions paid), no
  // renames, deletes or rate changes. RLS blocks those writes regardless.
  const readOnly = profile?.role === "viewer";
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Dialog & Form States ---
  const [openRateDialog, setOpenRateDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [newRate, setNewRate] = useState("");

  const [openNameDialog, setOpenNameDialog] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [newName, setNewName] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // --- Filters & Pagination State ---
  const [searchQuery, setSearchQuery] = useState("");
  const [showOutstandingOnly, setShowOutstandingOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // --- Payroll State ---
  const [payrollStart, setPayrollStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split("T")[0];
  });
  const [payrollEnd, setPayrollEnd] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [openPayrollDialog, setOpenPayrollDialog] = useState(false);
  const [sageTemplateData, setSageTemplateData] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewSessions, setReviewSessions] = useState([]);
  const [expandedTutorIds, setExpandedTutorIds] = useState(() => new Set());

  // ----------------------------------- Data Fetching ----------------------------------- //
  // Computed entirely server-side by get_tutor_overview (see
  // supabase/migrations/20260717120000_payroll_rpc_functions.sql) - this
  // queries and prices everything fresh at call time, so there's no
  // client-side calculation left that could go stale between page load and
  // whenever "Generate Payroll" is actually clicked.
  const fetchTutorsData = useCallback(async () => {
    setLoading(true);

    // Per-tutor rate exceptions (tutors.max_hourly_rate) aren't part of the
    // overview RPC - they're not a calculation - so they're read alongside it.
    const [{ data, error }, { data: rateCaps }] = await Promise.all([
      supabase.rpc("get_tutor_overview", {
        p_start: payrollStart,
        p_end: payrollEnd,
      }),
      supabase.from("tutors").select("id, max_hourly_rate"),
    ]);

    const maxRateByTutor = new Map(
      (rateCaps || []).map((t) => [t.id, t.max_hourly_rate]),
    );

    let formatted = null;
    if (error) {
      console.error("Error:", error);
    } else {
      formatted = (data || []).map((row) => ({
        id: row.tutor_id,
        name: row.name,
        email: row.email,
        totalApprovedHours: Number(row.total_approved_hours),
        totalEarnings: Number(row.total_earnings),
        assignments: row.assignments || [],
        maxHourlyRate: maxRateByTutor.get(row.tutor_id) ?? null,
      }));
      setTutors(formatted);
    }
    setLoading(false);
    // Return the freshly computed data so callers that need it *right now*
    // (like generating payroll) don't have to trust React state having
    // caught up yet - see processPayrollJoin.
    return formatted;
  }, [payrollStart, payrollEnd]);

  useEffect(() => {
    fetchTutorsData();
  }, [fetchTutorsData]);

  // Powers the review screen's per-tutor lesson breakdown - the exact same
  // session set (approved, unpaid, within the window) that
  // processAndDownloadPayroll will later hand to finalize_payroll, so what's
  // reviewed here is exactly what gets marked paid on confirm.
  const fetchPayrollSessions = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_payroll_sessions", {
      p_start: payrollStart,
      p_end: payrollEnd,
    });

    if (error) {
      console.error("Error fetching payroll sessions:", error);
      return [];
    }
    return data || [];
  }, [payrollStart, payrollEnd]);

  // ----------------------------------- Filter & Pagination Handlers ----------------------------------- //
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleToggleOutstandingOnly = (e) => {
    setShowOutstandingOnly(e.target.checked);
    setPage(0);
  };

  // ----------------------------------- UI & Copy Handlers ----------------------------------- //
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  // Ceiling for the assignment in the Set Rate dialog: R240, or the tutor's
  // approved exception (utils/rates.js).
  const rateDialogMax = maxRateFor(
    tutors.find((t) => t.id === selectedAssignment?.tutorId),
  );

  // ----------------------------------- Database Update Handlers ----------------------------------- //
  const handleUpdateRate = async () => {
    const rate = Number(newRate);

    if (!isValidHourlyRate(rate, rateDialogMax)) {
      alert(`Hourly Rate: ${hourlyRateRule(rateDialogMax)}`);
      return;
    }

    const { error } = await supabase
      .from("student_tutor_assignments")
      .update({ rate })
      .eq("id", selectedAssignment.id);

    if (!error) {
      setOpenRateDialog(false);
      setNewRate("");
      setSelectedAssignment(null);
      fetchTutorsData();
    } else {
      alert("Error updating rate: " + error.message);
    }
  };

  const handleNameUpdate = async () => {
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: newName })
      .eq("id", selectedTutor);

    if (error) {
      alert("Error updating name:", error.message);
    } else {
      setOpenNameDialog(false);
      fetchTutorsData();
    }
  };

  const handleDeleteTutor = async (id) => {
    if (
      window.confirm(
        "Delete tutor? This permanently removes them from the database.",
      )
    ) {
      if (window.confirm("This action cannot be undone. Proceed?")) {
        const { error } = await supabase.from("profiles").delete().eq("id", id);
        if (!error) fetchTutorsData();
      }
    }
  };

  const handleUnassignStudent = async (assignmentId) => {
    if (
      window.confirm(
        "Are you sure you want to unassign this student from this tutor?",
      )
    ) {
      const { error } = await supabase
        .from("student_tutor_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) alert("Error unassigning: " + error.message);
      else fetchTutorsData();
    }
  };

  // ----------------------------------- Payroll Handlers ----------------------------------- //
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);
      setSageTemplateData(json);
    };
    reader.readAsArrayBuffer(file);
  };

  const processPayrollJoin = async () => {
    setIsProcessing(true);
    try {
      // processAndDownloadPayroll calls get_tutor_overview / finalize_payroll
      // itself, fresh, at the moment it runs - so unlike before, nothing
      // here needs to hand it a pre-fetched `tutors` snapshot.
      const { updatedCount, sessions, confirmed } =
        await processAndDownloadPayroll(
          sageTemplateData,
          payrollStart,
          payrollEnd,
        );

      // Only generate the breakdown if the user actually confirmed marking
      // sessions paid - it's an audit trail of what got paid, so it
      // shouldn't appear if they cancelled that step. Built from the exact
      // `sessions` processAndDownloadPayroll already fetched (from before
      // finalize_payroll marked them paid), not a fresh query - see the
      // comment in payrollBreakdownService.js for why. Kept in its own
      // try/catch so a problem here can never block or corrupt the Sage
      // export/DB update above, which has already completed by this point.
      if (confirmed) {
        try {
          downloadTutorPayrollBreakdown(sessions, payrollStart, payrollEnd);
        } catch (breakdownErr) {
          console.error("Breakdown export failed:", breakdownErr);
          alert(
            "Payroll was processed, but the breakdown export failed: " +
              breakdownErr.message,
          );
        }
      }

      if (updatedCount > 0) {
        fetchTutorsData();
      }

      setOpenPayrollDialog(false);
      setSageTemplateData(null);
    } catch (err) {
      alert("Payroll Error: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // ----------------------------------- Derived State ----------------------------------- //
  const filteredTutors = tutors
    .filter(
      (tutor) =>
        tutor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tutor.email?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    // Scoped to the currently-selected payroll period (same
    // totalEarnings field get_tutor_overview computes for that window),
    // not an all-time outstanding balance.
    .filter((tutor) => !showOutstandingOnly || tutor.totalEarnings > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  const paginatedTutors = filteredTutors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Same "only tutors with outstanding earnings" filter used server-side by
  // processAndDownloadPayroll - shown here so the preview matches exactly
  // what will end up in the Sage export.
  const payableTutors = tutors
    .filter((t) => t.totalEarnings > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  const payrollTotal = payableTutors.reduce(
    (acc, t) => acc + t.totalEarnings,
    0,
  );

  // Sessions grouped by tutor for the review screen's expandable rows.
  const sessionsByTutor = reviewSessions.reduce((acc, s) => {
    (acc[s.tutor_id] ??= []).push(s);
    return acc;
  }, {});

  const toggleTutorExpanded = (tutorId) => {
    setExpandedTutorIds((prev) => {
      const next = new Set(prev);
      if (next.has(tutorId)) {
        next.delete(tutorId);
      } else {
        next.add(tutorId);
      }
      return next;
    });
  };

  // ----------------------------------- Render ----------------------------------- //
  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={20}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* TITLE AND PAYROLL BUTTON */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography
          variant="h4"
          color="primary.main"
          sx={{ fontWeight: "bold" }}
        >
          Tutor Management
        </Typography>
        {!readOnly && (
          <Button
            variant="contained"
            color="success"
            onClick={async () => {
              // Force a fresh calculation for the currently-selected dates
              // before showing the review screen - otherwise the preview
              // (and the export, if confirmed too quickly) could reflect
              // whatever date range was loaded previously instead of what's
              // on screen.
              setLoadingReview(true);
              setExpandedTutorIds(new Set());
              const [, sessions] = await Promise.all([
                fetchTutorsData(),
                fetchPayrollSessions(),
              ]);
              setReviewSessions(sessions);
              setLoadingReview(false);
              setOpenReviewDialog(true);
            }}
            disabled={isProcessing || loading || loadingReview}
            startIcon={
              isProcessing || loading || loadingReview ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PaidIcon />
              )
            }
            sx={{ fontWeight: "bold", px: 3 }}
          >
            {isProcessing
              ? "Processing..."
              : loading || loadingReview
                ? "Loading..."
                : "Generate Payroll"}
          </Button>
        )}
      </Stack>

      {/* FILTER BAR */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 4, bgcolor: "grey.50", borderRadius: 2 }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              sx={{ fontWeight: "500", mr: 1, color: "text.secondary" }}
            >
              Payroll Period:
            </Typography>
            <TextField
              label="From"
              type="date"
              size="small"
              value={payrollStart}
              onChange={(e) => setPayrollStart(e.target.value)}
              sx={{ width: 160, bgcolor: "white" }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={payrollEnd}
              onChange={(e) => setPayrollEnd(e.target.value)}
              sx={{ width: 160, bgcolor: "white" }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={showOutstandingOnly}
                onChange={handleToggleOutstandingOnly}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                Outstanding Payments
              </Typography>
            }
            sx={{ ml: { xs: 0, md: 1 } }}
          />
          <TextField
            size="small"
            placeholder="Search name or email..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: { xs: "100%", md: 350 }, bgcolor: "white" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Paper>

      {/* TUTOR LIST (Decoupled Component) */}
      {paginatedTutors.map((tutor) => (
        <TutorAccordion
          key={tutor.id}
          tutor={tutor}
          onEditName={(t) => {
            setSelectedTutor(t.id);
            setNewName(t.name);
            setOpenNameDialog(true);
          }}
          onCopyEmail={handleCopy}
          onDelete={handleDeleteTutor}
          onSetRate={(assignment) => {
            // The overview's assignment payload has no tutor id; it's needed
            // to look up this tutor's rate ceiling.
            setSelectedAssignment({ ...assignment, tutorId: tutor.id });
            setNewRate(assignment.rate);
            setOpenRateDialog(true);
          }}
          onUnassign={handleUnassignStudent}
          readOnly={readOnly}
        />
      ))}

      {filteredTutors.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#fafafa" }}>
          <Typography color="text.secondary">
            {searchQuery
              ? `No tutors found matching "${searchQuery}"`
              : "No tutors found."}
          </Typography>
        </Paper>
      )}

      {/* PAGINATION */}
      {filteredTutors.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, borderRadius: 1 }}>
          <TablePagination
            component="div"
            count={filteredTutors.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      )}

      {/* --- DIALOGS --- */}

      {/* Tutor Name Update Dialog */}
      <Dialog
        open={openNameDialog}
        onClose={() => setOpenNameDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Tutor Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the new display name for this tutor.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Tutor Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenNameDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleNameUpdate}
            variant="contained"
            color="primary"
            disabled={!newName.trim()}
          >
            Update Name
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Update Dialog */}
      <Dialog open={openRateDialog} onClose={() => setOpenRateDialog(false)}>
        <DialogTitle>Update Assignment Rate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set the hourly rate for{" "}
            <strong>{selectedAssignment?.students?.full_name}</strong>.
          </Typography>
          <TextField
            autoFocus
            label="Rate per Hour"
            type="number"
            fullWidth
            variant="outlined"
            helperText={hourlyRateRule(rateDialogMax)}
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            onBlur={(e) => {
              const rate = Number(e.target.value);

              if (Number.isFinite(rate)) {
                setNewRate(Math.round(rate));
              }
            }}
            slotProps={{
              htmlInput: {
                min: 130,
                max: rateDialogMax,
                step: 10,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenRateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateRate}
            disabled={!isValidHourlyRate(newRate, rateDialogMax)}
          >
            Save Rate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payroll Review Dialog - shows the per-tutor breakdown, and each
          tutor's individual lessons, for the currently-selected payroll
          period BEFORE anything is generated or marked paid. */}
      <Dialog
        open={openReviewDialog}
        onClose={() => setOpenReviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Payroll</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Payroll period: <strong>{payrollStart}</strong> to{" "}
              <strong>{payrollEnd}</strong>. Only tutors with outstanding
              approved, unpaid hours are shown below. Expand a tutor to see
              the individual lessons behind their total. Proceeding will mark
              every lesson shown here as <strong>paid</strong>.
            </Typography>

            {payableTutors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tutors have outstanding earnings for this period.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 420 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Tutor</TableCell>
                      <TableCell align="right">Approved Hours</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payableTutors.map((t) => {
                      const isExpanded = expandedTutorIds.has(t.id);
                      const lessons = sessionsByTutor[t.id] || [];

                      return (
                        <Fragment key={t.id}>
                          <TableRow hover>
                            <TableCell sx={{ width: 40 }}>
                              <IconButton
                                size="small"
                                onClick={() => toggleTutorExpanded(t.id)}
                                aria-label={
                                  isExpanded ? "Collapse" : "Expand"
                                }
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>{t.name}</TableCell>
                            <TableCell align="right">
                              {t.totalApprovedHours.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              R{t.totalEarnings.toLocaleString()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}
                              colSpan={4}
                            >
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ my: 1.5, ml: 5 }}>
                                  {lessons.length === 0 ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      No session-level detail available.
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Student</TableCell>
                                          <TableCell>Date</TableCell>
                                          <TableCell align="right">
                                            Hours
                                          </TableCell>
                                          <TableCell align="right">
                                            Rate
                                          </TableCell>
                                          <TableCell align="right">
                                            Amount
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {lessons.map((s) => (
                                          <TableRow key={s.log_id}>
                                            <TableCell>
                                              {s.student_name}
                                            </TableCell>
                                            <TableCell>
                                              {s.session_date}
                                            </TableCell>
                                            <TableCell align="right">
                                              {Number(s.hours)}
                                            </TableCell>
                                            <TableCell align="right">
                                              R{Number(s.rate)}
                                            </TableCell>
                                            <TableCell align="right">
                                              R{Number(s.amount)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="body2">
              Total: <strong>R{payrollTotal.toLocaleString()}</strong> across{" "}
              {payableTutors.length} tutor
              {payableTutors.length === 1 ? "" : "s"}.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReviewDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={payableTutors.length === 0}
            onClick={() => {
              setOpenReviewDialog(false);
              setOpenPayrollDialog(true);
            }}
          >
            Proceed to Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payroll Dialog */}
      <Dialog
        open={openPayrollDialog}
        onClose={() => setOpenPayrollDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Process Payroll</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              1. Upload your Sage .xlsx template.
              <br />
              2. A total of <strong>R{payrollTotal.toLocaleString()}</strong>{" "}
              across {payableTutors.length} tutor
              {payableTutors.length === 1 ? "" : "s"} will be filled into the
              "Amount" column, and those lessons will be marked as paid.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              {sageTemplateData ? "File Ready" : "Upload Sage Template"}
              <input
                type="file"
                hidden
                accept=".xlsx"
                onChange={handleFileUpload}
              />
            </Button>
            {sageTemplateData && (
              <Typography variant="caption" color="success.main">
                Template loaded successfully.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayrollDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!sageTemplateData || isProcessing}
            onClick={processPayrollJoin}
          >
            {isProcessing ? "Processing..." : "Confirm & Download"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Email copied to clipboard!
        </Alert>
      </Snackbar>
    </Container>
  );
}
